// Funnel behaviour, shared by both steps of the two-page flow:
//
//   Step 1  /pages/<lead-page>     lead capture -> redirect to the product
//   Step 2  /products/<product>    sales page, purchase, sticky bar, FAQ
//
// Product-agnostic — nothing here is specific to one funnel, so future
// digital-product funnels reuse this file unchanged.
//
// Structurally modelled on assets/coaching-program.js (shared static state
// across every purchase instance so the main CTA and the sticky bar can never
// drift apart), but it is a separate implementation and does not touch it.

(function () {
  var LEAD_PREFIX = 'corey_funnel_lead:';
  var PENDING_PREFIX = 'corey_funnel_pending:';
  var LEAD_FORWARD_GUARD = 'corey_funnel_lead_forwarded';
  var ATTR_KEY = 'corey_funnel_attribution';
  var REDIRECT_GUARD = 'corey_funnel_redirected';

  /* ------------------------------------------------------------------ */
  /* Storage helpers — every access is guarded                           */
  /* ------------------------------------------------------------------ */
  // Private mode and locked-down contexts throw on access, not just on write.
  // Losing this state only means the visitor sees Step 1 again, so every
  // failure path degrades to "no record" rather than breaking the page.
  function readJSON(key) {
    try {
      var raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function writeJSON(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  }

  function getLead(funnelKey) {
    return readJSON(LEAD_PREFIX + funnelKey);
  }

  /* ------------------------------------------------------------------ */
  /* Attribution                                                         */
  /* ------------------------------------------------------------------ */
  // Captured on first landing and preserved across both pages, because the ad
  // parameters exist only on the Step 1 URL and are gone by the time the
  // visitor reaches checkout. First touch wins: a later internal navigation
  // must not overwrite the original ad source.
  var ATTR_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'fbclid',
    'gclid',
  ];

  function sessionId() {
    try {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    } catch (error) {
      /* fall through */
    }
    return 'fs-' + Date.now().toString(36);
  }

  function captureAttribution() {
    var existing = readJSON(ATTR_KEY);
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      return existing || {};
    }

    var incoming = {};
    var sawAny = false;
    ATTR_PARAMS.forEach(function (name) {
      var value = params.get(name);
      if (value) {
        incoming[name] = value;
        sawAny = true;
      }
    });

    // Keep the first recorded touch unless this visit carries fresh ad
    // parameters of its own.
    if (existing && !sawAny) return existing;

    var record = existing && !sawAny ? existing : incoming;
    record.referrer = (existing && existing.referrer) || document.referrer || '';
    record.funnel_session_id = (existing && existing.funnel_session_id) || sessionId();
    record.first_seen_at = (existing && existing.first_seen_at) || new Date().toISOString();
    writeJSON(ATTR_KEY, record);
    return record;
  }

  /* ------------------------------------------------------------------ */
  /* STEP 1 — lead capture page                                          */
  /* ------------------------------------------------------------------ */
  function initLeadPage() {
    var root = document.querySelector('[data-funnel-lead-page]');
    if (!root) return;

    var funnelKey = root.dataset.funnelKey || 'funnel';
    var destination = root.dataset.destinationUrl || '';
    var attribution = captureAttribution();

    var form = root.querySelector('form');
    var submit = root.querySelector('[data-funnel-lead-submit]');
    var errorEl = root.querySelector('[data-funnel-lead-error]');

    // ---- Success state -------------------------------------------------
    // Reached after Shopify's own POST/redirect cycle, signalled by the
    // server-rendered success block (form.posted_successfully?) or by
    // ?customer_posted=true on the URL. Shopify treats a repeat email as a
    // success and updates the existing record, so an existing customer lands
    // here too — no duplicate is created and no failure is shown.
    var posted = Boolean(root.querySelector('[data-funnel-lead-success]')) || hasPostedFlag();

    if (posted) {
      promotePendingLead(funnelKey, attribution);

      // Auto-forward only once per tab. Without this, pressing Back from the
      // product page returns here with ?customer_posted=true still on the URL
      // and immediately throws the visitor forward again — a trap with no way
      // back. On any later visit the server-rendered Continue link is used
      // instead, so the visitor stays in control.
      var alreadyForwarded = false;
      try {
        alreadyForwarded = window.sessionStorage.getItem(LEAD_FORWARD_GUARD) === funnelKey;
        window.sessionStorage.setItem(LEAD_FORWARD_GUARD, funnelKey);
      } catch (error) {
        /* storage unavailable — fall through and forward once */
      }

      if (destination && !alreadyForwarded) {
        // replace() so the POSTed URL never becomes a Back target.
        window.location.replace(withSession(destination, attribution));
      }
      return;
    }

    if (!form || !submit) return;

    // ---- Native submit -------------------------------------------------
    // The form is NOT intercepted. Shopify's first-party hCaptcha script
    // (cdn.shopify.com/shopifycloud/storefront-forms-hcaptcha/...) binds its
    // own submit listener and calls preventDefault() until it has a token.
    // A fetch() POST bypasses that binding entirely and arrives without
    // `h-captcha-response`, which Shopify rejects — that was the real cause of
    // the "we couldn't save those details" failure. Letting the form post
    // natively keeps spam protection fully intact and costs one page load.
    form.addEventListener('submit', function (event) {
      // Field values must be captured before the page unloads: the native
      // POST navigates away and nothing survives the round trip otherwise.
      // Saved even when the submit is prevented, since hCaptcha may re-submit
      // the form itself.
      savePendingLead(funnelKey, {
        firstName: valueOf(form, 'contact[first_name]'),
        lastName: valueOf(form, 'contact[last_name]'),
        email: valueOf(form, 'contact[email]'),
        acceptsMarketing: Boolean(form.querySelector('[name="contact[accepts_marketing]"]:checked')),
      });

      // hCaptcha cancelled this submit to show its challenge, or the browser
      // blocked it on native validation. Either way the page is staying put,
      // so the button must not be left in a loading state.
      if (event.defaultPrevented || !form.checkValidity()) return;

      if (errorEl) errorEl.hidden = true;
      setBusy(true);
    });

    // Returning via the bfcache would otherwise restore a button still stuck
    // in its loading state from the submit that navigated away.
    window.addEventListener('pageshow', function () {
      setBusy(false);
    });

    function setBusy(isBusy) {
      if (!submit) return;
      if (isBusy) {
        submit.dataset.restoreLabel = submit.textContent;
        submit.textContent = 'One moment…';
      } else if (submit.dataset.restoreLabel) {
        submit.textContent = submit.dataset.restoreLabel;
        delete submit.dataset.restoreLabel;
      }
      submit.setAttribute('aria-busy', String(isBusy));
      // Deliberately NOT disabled: a disabled submit button can suppress the
      // native submission that is now doing the actual work.
      submit.classList.toggle('is-busy', isBusy);
    }
  }

  function valueOf(form, name) {
    var field = form.querySelector('[name="' + name + '"]');
    return field ? field.value : '';
  }

  function hasPostedFlag() {
    try {
      return new URLSearchParams(window.location.search).get('customer_posted') === 'true';
    } catch (error) {
      return window.location.search.indexOf('customer_posted=true') !== -1;
    }
  }

  // Written before the native POST navigates away.
  function savePendingLead(funnelKey, details) {
    writeJSON(PENDING_PREFIX + funnelKey, details);
  }

  // Promoted to a confirmed lead only once Shopify has actually reported
  // success, so a submission that hCaptcha or validation rejected never
  // records a lead that does not exist.
  function promotePendingLead(funnelKey, attribution) {
    var details = readJSON(PENDING_PREFIX + funnelKey) || {};
    writeJSON(LEAD_PREFIX + funnelKey, {
      firstName: details.firstName || '',
      lastName: details.lastName || '',
      email: details.email || '',
      acceptsMarketing: Boolean(details.acceptsMarketing),
      created_at: new Date().toISOString(),
      attribution: attribution || {},
    });
    try {
      window.localStorage.removeItem(PENDING_PREFIX + funnelKey);
    } catch (error) {
      /* nothing to clean up */
    }
  }

  // The session parameter is a redirect-survival hint, not proof of anything.
  // It lets Step 2 render immediately even when localStorage is unavailable,
  // which is what stops the two pages bouncing a visitor back and forth.
  function withSession(url, attribution) {
    var id = (attribution && attribution.funnel_session_id) || '';
    if (!id) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'fs=' + encodeURIComponent(id);
  }

  /* ------------------------------------------------------------------ */
  /* STEP 2 — gate on the paid product page                              */
  /* ------------------------------------------------------------------ */
  function initProductGate() {
    var root = document.querySelector('[data-funnel]');
    if (!root) return;

    var funnelKey = root.dataset.funnelKey || 'funnel';
    var leadPageUrl = root.dataset.leadPageUrl || '';
    var gateEnabled = root.dataset.gateEnabled === 'true';
    var customerLoggedIn = root.dataset.customerLoggedIn === 'true';

    captureAttribution();

    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      params = null;
    }
    var hasSession = params ? Boolean(params.get('fs')) : false;
    var bypass = params ? params.get('nogate') === '1' : false;

    // Arriving with a session parameter means Step 1 just completed; record it
    // so a refresh or a back-and-forward does not bounce the visitor.
    if (hasSession && !getLead(funnelKey)) {
      writeJSON(LEAD_PREFIX + funnelKey, {
        email: '',
        created_at: new Date().toISOString(),
        via: 'session-param',
      });
    }

    if (!gateEnabled || !leadPageUrl) return;

    // A purchaser returning through their order email, or anyone with a
    // recorded lead, stays. Nobody who paid should ever be bounced.
    if (customerLoggedIn || bypass || hasSession || getLead(funnelKey)) return;

    // Loop breaker: if we already sent this tab to Step 1 once, render the page
    // rather than ping-ponging. Storage being unavailable is exactly the case
    // where a naive gate loops forever.
    try {
      if (window.sessionStorage.getItem(REDIRECT_GUARD) === funnelKey) return;
      window.sessionStorage.setItem(REDIRECT_GUARD, funnelKey);
    } catch (error) {
      return; // cannot track the guard, so do not risk a loop
    }

    window.location.replace(leadPageUrl);
  }

  /* ------------------------------------------------------------------ */
  /* Purchase                                                            */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('funnel-purchase')) {
    customElements.define(
      'funnel-purchase',
      class FunnelPurchase extends HTMLElement {
        static instances = [];
        static isSubmitting = false;

        connectedCallback() {
          this.form = this.querySelector('form');
          this.cta = this.querySelector('[data-funnel-purchase-cta]');
          this.statusEl = this.querySelector('[data-funnel-purchase-status]');
          this.errorEl = this.querySelector('[data-funnel-purchase-error]');

          this.variantId = this.dataset.variantId || '';
          this.variantAvailable = this.dataset.variantAvailable === 'true';
          this.checkoutEnabled = this.dataset.checkoutEnabled === 'true';

          FunnelPurchase.instances.push(this);

          this.form?.addEventListener('submit', (event) => {
            event.preventDefault();
            FunnelPurchase.submit();
          });
        }

        disconnectedCallback() {
          FunnelPurchase.instances = FunnelPurchase.instances.filter((i) => i !== this);
        }

        // Every placement shares one static state, so the main CTA and the
        // sticky bar can never both be armed and add the item twice.
        static setBusy(isBusy) {
          FunnelPurchase.instances.forEach((instance) => {
            if (!instance.cta) return;
            if (isBusy) {
              instance.cta.dataset.restoreLabel = instance.cta.textContent;
              instance.cta.textContent = 'Taking you to checkout…';
            } else if (instance.cta.dataset.restoreLabel) {
              instance.cta.textContent = instance.cta.dataset.restoreLabel;
              delete instance.cta.dataset.restoreLabel;
            }
            instance.cta.setAttribute('aria-busy', String(isBusy));
            instance.cta.disabled = isBusy;
            if (instance.statusEl) instance.statusEl.hidden = !isBusy;
          });
        }

        static showError(message) {
          FunnelPurchase.instances.forEach((instance) => {
            if (!instance.errorEl) return;
            instance.errorEl.textContent = message;
            instance.errorEl.hidden = false;
          });
        }

        static clearError() {
          FunnelPurchase.instances.forEach((instance) => {
            if (instance.errorEl) instance.errorEl.hidden = true;
          });
        }

        // Cart attributes are the supported way to carry the lead and its ad
        // attribution onto the order. Shopify has no supported storefront
        // method for pre-filling the checkout email address, so none is faked
        // here — a query parameter that Shopify does not honour would look like
        // it worked while quietly doing nothing.
        static cartAttributes() {
          var root = document.querySelector('[data-funnel]');
          var funnelKey = root ? root.dataset.funnelKey || 'funnel' : 'funnel';
          var lead = getLead(funnelKey) || {};
          var attribution = readJSON(ATTR_KEY) || {};

          var attributes = {};
          if (lead.email) attributes['Lead email'] = lead.email;
          if (lead.firstName) attributes['Lead first name'] = lead.firstName;
          if (lead.lastName) attributes['Lead last name'] = lead.lastName;
          if (lead.created_at) attributes['Lead created at'] = lead.created_at;

          Object.keys(attribution).forEach(function (key) {
            if (attribution[key]) attributes[key] = String(attribution[key]);
          });

          return attributes;
        }

        static async submit() {
          if (FunnelPurchase.isSubmitting) return;

          const first = FunnelPurchase.instances[0];
          if (!first) return;

          FunnelPurchase.clearError();

          if (!first.checkoutEnabled || !first.variantId) {
            FunnelPurchase.showError('Checkout is not connected yet on this preview.');
            return;
          }

          if (!first.variantAvailable) {
            FunnelPurchase.showError("This isn't available right now. Please check back soon.");
            return;
          }

          FunnelPurchase.isSubmitting = true;
          FunnelPurchase.setBusy(true);

          try {
            // Isolated-funnel behaviour: checkout contains only this product.
            // Correct for a single-product ad funnel, but it does discard
            // anything already in the visitor's cart from the main site.
            await fetch('/cart/clear.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            });

            const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
            const response = await fetch(addUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: first.variantId, quantity: 1 }] }),
            });

            if (!response.ok) {
              const payload = await response.json().catch(() => null);
              throw new Error(payload?.description || 'Something went wrong starting your checkout.');
            }

            // Attributes are written after the add, because clearing the cart
            // also clears them. A failure here must not block the purchase.
            const attributes = FunnelPurchase.cartAttributes();
            if (Object.keys(attributes).length) {
              await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ attributes }),
              }).catch(() => null);
            }

            // Straight to checkout, never the cart page — the cart is a
            // competing CTA and this page has no navigation by design.
            window.location.href = '/checkout';
          } catch (error) {
            FunnelPurchase.showError(error.message || 'Something went wrong. Please try again.');
            FunnelPurchase.isSubmitting = false;
            FunnelPurchase.setBusy(false);
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /* Mobile sticky bar                                                   */
  /* ------------------------------------------------------------------ */
  function initSticky() {
    var sticky = document.querySelector('[data-funnel-sticky]');
    var main = document.querySelector('[data-funnel-purchase-placement="main"]');
    if (!sticky || !main || sticky.dataset.enabled === undefined) return;

    var observer = new IntersectionObserver(
      function (entries) {
        sticky.classList.toggle('is-visible', !entries[0].isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(main);

    sticky.querySelector('form')?.addEventListener('submit', function () {
      sticky.classList.remove('is-visible');
    });
  }

  /* ------------------------------------------------------------------ */
  /* FAQ accordion                                                       */
  /* ------------------------------------------------------------------ */
  // Collapsed panels are marked `inert` as well as clipped, so their content
  // leaves the tab order and the accessibility tree rather than staying
  // screen-reader reachable behind a max-height:0.
  function initFaqs() {
    document.querySelectorAll('[data-funnel-faq-trigger]').forEach(function (trigger) {
      var panelId = trigger.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : null;
      panel?.setAttribute('inert', '');

      trigger.addEventListener('click', function () {
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!expanded));
        if (!panel) return;
        if (expanded) {
          panel.removeAttribute('data-open');
          panel.setAttribute('inert', '');
        } else {
          panel.setAttribute('data-open', '');
          panel.removeAttribute('inert');
        }
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Inside the Guide carousel                                           */
  /* ------------------------------------------------------------------ */
  // Progressive enhancement only. The track is a CSS scroll-snap rail that is
  // already swipeable and keyboard-scrollable on its own; these arrows are a
  // desktop convenience and nothing breaks without them.
  function initCarousel() {
    document.querySelectorAll('[data-funnel-carousel]').forEach(function (track) {
      var wrap = track.parentElement;
      if (!wrap) return;
      var prev = wrap.querySelector('[data-funnel-carousel-prev]');
      var next = wrap.querySelector('[data-funnel-carousel-next]');
      var step = function () {
        var item = track.querySelector('.funnel-carousel__item');
        return item ? item.getBoundingClientRect().width + 12 : track.clientWidth * 0.7;
      };
      prev && prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      next && next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
    });
  }

  function init() {
    initLeadPage();
    initCarousel();
    initProductGate();
    initSticky();
    initFaqs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
